// @ts-strict-ignore
import { Features } from '@backend/lib-feature-mgr';
import { useCallback, useState } from 'react';

import { AutocompleteSelect } from '~/components/AutocompleteSelect';
import type { SelectOption } from '~/components/Select';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useSearchFormContext } from '~/contexts/SearchFormContext/SearchFormContext';
import { useCacheFromCallback, useCheckFeatureEnabled, useDebouncedCallback, useTranslation } from '~/hooks';
import { callIndexerProxy } from '~/utils/indexer';

type JobSelectProps = {
  fontSize?: 'l' | 'm';
};

export const JobSelect = ({ fontSize }: JobSelectProps) => {
  const t = useTranslation();
  const { businessUnit } = useBusinessUnitContext();
  const [options, setOptions] = useState<SelectOption[]>([]);
  const externalJobOffersFeature = useCheckFeatureEnabled(Features.ExternalJobOffers);

  const {
    form: {
      job: { value, onChange, onValidityChange },
    },
  } = useSearchFormContext();

  const getJobs = useCacheFromCallback(async (needle: string) => {
    const jobsEndpoint = externalJobOffersFeature ? 'jobOfferSearch/titleAutocomplete' : 'jobSearch/searchForJobs';

    const { data } = await callIndexerProxy<string>(businessUnit, jobsEndpoint, { needle });

    return data?.results || [];
  });

  const [debounceJobs, cancelDebounceJobs] = useDebouncedCallback(async (needle: string) => {
    // Suggestions are a convenience: a lookup that fails (a mobile connection dropping, the proxy
    // answering an error it has logged) leaves the list empty, and stays out of the cache so the
    // same search is looked up again.
    const jobs = await getJobs(needle).catch((): string[] => []);
    setOptions(jobs.map(job => ({ value: job })));
  }, 800);

  const handleSearchChange = useCallback(async (newSearch: string | null) => {
    if (newSearch?.length < 2) {
      cancelDebounceJobs();
      setOptions([]);
      return;
    }
    await debounceJobs(newSearch);
  }, []);

  return (
    <AutocompleteSelect
      clearable
      placeholder={t('form_placeholders.job')}
      allowFreeText
      value={value}
      options={options}
      fontSize={fontSize}
      onChange={onChange}
      resetSearchOnFocus={false}
      onSearchChange={handleSearchChange}
      onValidityChange={onValidityChange}
    />
  );
};
